/**
 * Browser-side image pyramid. The server never has to decode a huge file:
 * we produce WebP variants here and (in Phase 2) upload them next to the original.
 */

export const VARIANT_SIZES = { thumb: 256, medium: 1024, large: 2560 } as const;
export type VariantName = keyof typeof VARIANT_SIZES | 'original';
export const VARIANT_ORDER: VariantName[] = ['thumb', 'medium', 'large', 'original'];

export interface VariantBlob {
  blob: Blob;
  w: number;
  h: number;
}

export interface GeneratedVariants {
  width: number;
  height: number;
  variants: Partial<Record<Exclude<VariantName, 'original'>, VariantBlob>>;
}

/** Smallest variant whose longest side covers `neededPx`, else the original. */
export function pickVariant(neededPx: number): VariantName {
  if (neededPx <= VARIANT_SIZES.thumb) return 'thumb';
  if (neededPx <= VARIANT_SIZES.medium) return 'medium';
  if (neededPx <= VARIANT_SIZES.large) return 'large';
  return 'original';
}

export async function generateVariants(file: Blob): Promise<GeneratedVariants> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const width = bitmap.width;
    const height = bitmap.height;
    const longest = Math.max(width, height);
    const out: GeneratedVariants = { width, height, variants: {} };

    // Generate from largest to smallest, each step downscaling the previous result for quality.
    let source: ImageBitmap | HTMLCanvasElement = bitmap;
    for (const name of ['large', 'medium', 'thumb'] as const) {
      const max = VARIANT_SIZES[name];
      if (longest <= max) continue; // the original already is small enough for this level
      const scale = max / longest;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = downscale(source, w, h);
      const blob = await canvasToBlob(canvas, 'image/webp', 0.86);
      out.variants[name] = { blob, w, h };
      source = canvas;
    }
    return out;
  } finally {
    bitmap.close();
  }
}

/** Stepwise halving before the final resample avoids aliasing on big downscales. */
function downscale(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  let cur: ImageBitmap | HTMLCanvasElement = source;
  let cw = source.width;
  let ch = source.height;
  while (cw / 2 >= w && ch / 2 >= h) {
    cw = Math.floor(cw / 2);
    ch = Math.floor(ch / 2);
    cur = draw(cur, cw, ch);
  }
  return cur.width === w && cur.height === h && cur instanceof HTMLCanvasElement ? cur : draw(cur, w, h);
}

function draw(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
  });
}
